import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import DataTable from 'react-data-table-component';
import { t } from '../i18n';
const apiUrl = process.env.REACT_APP_API_URL;

export default function ProductImportPage() {
  const navigate = useNavigate();
  const [importData, setImportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [importStatus, setImportStatus] = useState('');
  const fileInputRef = React.useRef(null);

  const breadcrumbPaths = [
    { label: t('Продукти'), to: '/products' },
    { label: t('Листа на производи'), to: '/products/list' },
    { label: t('Импорт') }
  ];

  const tableColumns = [
    { name: t('Ред'), selector: (row, index) => index + 1, sortable: false, width: '60px' },
    { name: t('Назив'), selector: row => row.name || '', sortable: false },
    { name: t('Набавна цена'), selector: row => row.cost_price || '', sortable: false },
    { name: t('ДДВ'), selector: row => row.tax || '', sortable: false },
    { name: t('Единица'), selector: row => row.unit || '', sortable: false },
    { name: t('Производител'), selector: row => row.manufacturer || '', sortable: false },
    { name: t('Категорија'), selector: row => row.category || '', sortable: false },
    { name: t('Статус'), 
      selector: row => row._validation_status || 'Valid', 
      sortable: false,
      cell: row => (
        <span className={`px-2 py-1 text-xs rounded ${
          row._validation_status === 'Error' 
            ? 'bg-red-100 text-red-800' 
            : 'bg-green-100 text-green-800'
        }`}>
          {row._validation_status === 'Error' ? t('Грешка') : t('Валиден')}
        </span>
      )
    }
  ];

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let data = [];
        
        if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
          // Handle CSV
          const text = e.target.result;
          const lines = text.split('\n');
          const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
          
          for (let i = 1; i < lines.length; i++) {
            if (lines[i].trim()) {
              const values = lines[i].split(',');
              const row = {};
              headers.forEach((header, index) => {
                row[header] = values[index]?.trim() || '';
              });
              data.push(row);
            }
          }
        } else {
          // Handle Excel
          const workbook = XLSX.read(e.target.result, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (data.length > 0) {
            const headers = data[0].map(h => String(h).toLowerCase().trim());
            const rows = data.slice(1).map(row => {
              const obj = {};
              headers.forEach((header, index) => {
                obj[header] = row[index] || '';
              });
              return obj;
            }).filter(row => Object.values(row).some(val => val !== ''));
            data = rows;
          }
        }

        // Validate data
        const validatedData = validateImportData(data);
        setImportData(validatedData);
        setImportStatus(t('Вчитани {count} редови', { count: validatedData.length }));
        
      } catch (error) {
        console.error('Error parsing file:', error);
        setImportStatus(t('Грешка при читање на датотеката. Проверете го форматот.'));
      }
    };

    if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
      reader.readAsText(file);
    } else {
      reader.readAsBinaryString(file);
    }
  };

  const validateImportData = (data) => {
    const errors = [];
    
    return data.map((row, index) => {
      const rowErrors = [];
      
      // Check required fields
      if (!row.name || row.name.trim() === '') {
        rowErrors.push(t('Називот е задолжителен'));
      }
      
      if (row.price && isNaN(parseFloat(row.price))) {
        rowErrors.push(t('Цената мора да биде број'));
      }
      
      if (row.cost_price && isNaN(parseFloat(row.cost_price))) {
        rowErrors.push(t('Набавната цена мора да биде број'));
      }
      
      if (row.tax && (isNaN(parseFloat(row.tax)) || parseFloat(row.tax) < 0 || parseFloat(row.tax) > 100)) {
        rowErrors.push(t('ДДВ мора да биде број помеѓу 0 и 100'));
      }

      if (rowErrors.length > 0) {
        errors.push({ row: index + 1, errors: rowErrors });
        return { ...row, _validation_status: 'Error', _validation_errors: rowErrors };
      }
      
      return { ...row, _validation_status: 'Valid' };
    });
  };

  const handleImport = async () => {
    if (importData.length === 0) {
      setImportStatus(t('Нема податоци за увоз'));
      return;
    }

    const validRows = importData.filter(row => row._validation_status === 'Valid');
    if (validRows.length === 0) {
      setImportStatus(t('Нема валидни редови за увоз'));
      return;
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/products/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ products: validRows })
      });

      if (response.ok) {
        const result = await response.json();
        setImportStatus({
          message: t('Успешно увезени {count} производи', { count: result.imported }) + (result.errors ? '. ' + t('{count} редови имаа грешки.', { count: result.errors.length }) : ''),
          isError: result.success === false
        });
        if (result.success) {
          setTimeout(() => navigate('/products'), 2000);
        }
      } else {
        const error = await response.json();
        setImportStatus({
          message: t('Увозот не успеа: {message}', { message: error.message }),
          isError: true
        });
      }
    } catch (error) {
      console.error('Import error:', error);
      setImportStatus(t('Увозот не успеа. Обидете се повторно.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setImportData([]);
    setImportStatus('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const templateData = [
      ['name', 'cost_price', 'tax', 'unit', 'manufacturer', 'category'],
      ['Sample Product', '75.00', '18', 'pcs', 'Sample Manufacturer', 'Sample Category']
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Производи');
    XLSX.writeFile(wb, 'products_template.xlsx');
  };

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{t('Увоз на производи')}</h1>
        </div>

        {/* File Upload Section */}
        <div className="mb-6 p-4 border-2 border-dashed border-gray-300 rounded-lg">
          <div className="text-center">
            <div className="mb-4">
              <label htmlFor="file-upload" className="cursor-pointer">
                <div className="mx-auto w-12 h-12 text-gray-400 mb-4">
                  📁
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-medium text-indigo-600 hover:text-indigo-500">
                    {t('Кликнете за прикачување')}
                  </span>{' '}
                  {t('или повлечете и пуштете')}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {t('CSV или Excel датотеки (XLS, XLSX)')}
                </p>
              </label>
              <input
                ref={fileInputRef}
                id="file-upload"
                name="file-upload"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileUpload}
                className="sr-only"
              />
            </div>
            
            <div className="flex justify-center gap-4">
              <button
                onClick={downloadTemplate}
                className="px-4 py-2 text-sm text-indigo-600 hover:text-indigo-800 border border-indigo-600 rounded"
              >
                {t('Преземи шаблон')}
              </button>
            </div>
          </div>
        </div>

        {/* Status Message */}
        {importStatus && (
          typeof importStatus === 'string' ? (
            <div className="mb-4 p-3 rounded bg-green-100 text-green-700">
              {importStatus}
            </div>
          ) : (
            <div className={`mb-4 p-3 rounded ${
              importStatus.isError
                ? 'bg-red-100 text-red-700'
                : 'bg-green-100 text-green-700'
            }`}>
              {importStatus.message}
            </div>
          )
        )}

        {/* Data Preview Table */}
        {importData.length > 0 && (
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">{t('Преглед на податоци')}</h2>
              <div className="flex gap-2">
                <button
                  onClick={handleClear}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 border rounded"
                >
                  {t('Исчисти')}
                </button>
                <button
                  onClick={handleImport}
                  disabled={isLoading || importData.filter(row => row._validation_status === 'Valid').length === 0}
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isLoading ? t('Се увезува...') : t('Увези {count} производи', { count: importData.filter(row => row._validation_status === 'Valid').length })}
                </button>
              </div>
            </div>
            
            <DataTable
              columns={tableColumns}
              data={importData}
              pagination
              highlightOnHover
              striped
              responsive
              noDataComponent={t('Нема податоци за преглед')}
            />
          </div>
        )}

        {/* Instructions */}
        <div className="bg-gray-50 rounded p-4">
          <h3 className="font-semibold mb-2">{t('Упатство за увоз:')}</h3>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• {t('Прикачете CSV или Excel датотека со податоци за производи')}</li>
            <li>• {t('Задолжителни колони: name')}</li>
            <li>• {t('Опционални колони: cost_price, tax, unit, manufacturer, category')}</li>
            <li>• {t('Преземете го шаблонот за правилен формат')}</li>
            <li>• {t('Прегледајте ги податоците пред увоз')}</li>
            <li>• {t('Ќе се увезат само валидните редови')}</li>
          </ul>
        </div>
      </div>
    </div>
  );
}